import { defineStore } from 'pinia';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, FormatedEnum } from 'src/core/types/enum';
import { ResponseDataPreparationEnums } from 'src/core/types/enum/dataPreparation';

type DataPreparationEnumsStore = {
  enums: ResponseDataPreparationEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing data preparation enums data.
 */
export const useDataPreparationEnumsStore = defineStore(
  'dataPreparationEnums',
  {
    state: () =>
      ({
        enums: {} as ResponseDataPreparationEnums,
        formatedEnums: [] as FormatedEnum[],
      } as DataPreparationEnumsStore),

    getters: {
      /**
       * Get the formatted value of an enum.
       * @param enumVal - The enum value to format.
       * @returns The formatted enum value.
       */
      getFormatedEnumValue: (state) => (enumVal: string) => {
        return (
          state.formatedEnums.find(
            (enumValue: FormatedEnum) => enumValue[enumVal]
          )?.[enumVal] || enumVal
        );
      },

      /**
       * Get the enum instance based on the enum value.
       * @param enumVal - The enum value to search for.
       * @returns The enum value if found, undefined otherwise.
       */
      getEnumInstance: (state) => (enumVal: string) => {
        let foundEnum: EnumType | undefined;
        Object.keys(state.enums).forEach((key: string) => {
          if (foundEnum) return;
          foundEnum = state.enums[key as keyof typeof state.enums].find(
            (enumValue) => enumValue.value === enumVal
          );
        });
        return foundEnum;
      },
    },

    actions: {
      /**
       * Get enums information.
       */
      async getEnums() {
        const apiStore = useApiStore();
        const { handleError } = useErrorHandling();
        try {
          const responseEnums =
            await apiStore.service.get<ResponseDataPreparationEnums>(
              '/data-preparation/enums'
            );
          const responseEnumsSorted = Object.fromEntries(
            Object.entries(responseEnums.data).map(([key, value]) => [
              key,
              value.sort((a, b) => a.label.localeCompare(b.label)),
            ])
          );
          this.formatedEnums = [];
          this.enums = responseEnumsSorted;

          Object.keys(responseEnumsSorted).forEach((key: string) => {
            responseEnumsSorted[
              key as keyof ResponseDataPreparationEnums
            ].forEach((enumValue: EnumType) => {
              this.formatedEnums.push({ [enumValue.value]: enumValue.label });
            });
          });
        } catch (e) {
          handleError('Enums error', e);
        }
      },
    },
  }
);
